import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CurrencyPipe, isPlatformBrowser } from '@angular/common';
import {
  Component,
  computed,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ActiveBookingIntentService,
  ActiveProviderService,
  BookingIntentsService,
  CHECKOUT_SERVICE,
  ToastService,
} from '@services';
import {
  BookAppointmentPatientDialogComponent,
  ConfirmDialogComponent,
  DisclaimersDialogComponent,
  DisplayAvatarComponent,
  SpinnerComponent,
} from '@ui-lib';
import { catchError, EMPTY, finalize, of, switchMap, take, tap } from 'rxjs';

export interface SetupPaymentMethodDialogData {
  itemId: string;
  servicesCost: number | string;
  taxAmountCents?: number;
  taxPercentage?: number | null;
}

@Component({
  selector: 'icaredx-setup-payment-method-dialog',
  imports: [DisplayAvatarComponent, CurrencyPipe, SpinnerComponent],
  templateUrl: './setup-payment-method-dialog.component.html',
})
export class SetupPaymentMethodDialogComponent {
  private readonly activeProvider = inject(ActiveProviderService);
  private readonly dialog = inject(Dialog);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly checkoutService = inject(CHECKOUT_SERVICE);
  private readonly toast = inject(ToastService);
  private readonly dialogRef = inject(DialogRef);
  private readonly bookingIntentsService = inject(BookingIntentsService);
  private readonly activeIntentService = inject(ActiveBookingIntentService);

  readonly data: SetupPaymentMethodDialogData = inject(DIALOG_DATA);

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  submitting = signal(false);

  provider = toSignal(this.activeProvider.getActiveProviderData());

  readonly taxAmountDollars = computed(() => {
    if (this.data.taxAmountCents != null) {
      return this.data.taxAmountCents / 100;
    }

    if (this.data.taxPercentage == null) {
      return null;
    }

    const subtotalCents = Math.round(Number(this.data.servicesCost ?? 0) * 100);
    const taxAmountCents = Math.round(
      (subtotalCents * this.data.taxPercentage) / 100
    );

    return taxAmountCents / 100;
  });

  readonly taxLabel = computed(() => {
    return this.data.taxPercentage != null
      ? `Tax (${this.data.taxPercentage}%)`
      : 'Tax';
  });

  get providerNameSplited() {
    const [givenName, familyName] = this.provider()!.providerName.split(' ');

    return {
      givenName: givenName,
      familyName: familyName,
    };
  }

  openDisclaimer(type: 'privacyPolicy' | 'termsOfService') {
    if (this.isBrowser) {
      this.dialog.open(DisclaimersDialogComponent, {
        data: { disclaimerType: type },
      });
    }
  }

  submit() {
    this.submitting.set(true);
    this.checkoutService
      .createSetupSession({
        itemId: this.data.itemId,
        providerId: this.provider()!.providerId,
      })
      .pipe(
        catchError(err => {
          if (
            err?.status === 409 &&
            err.error.code == 'CHECKOUT_SESSION_IS_ALREADY_COMPLETED'
          ) {
            this.toast.showSuccess('Payment method has already been saved!');
            this.dialogRef.close(true);
          }
          return EMPTY;
        }),
        finalize(() => this.submitting.set(false))
      )
      .subscribe(result => {
        if (result) {
          window.location.href = result.url;
        }
      });
  }

  cancelBooking(): void {
    const intentId = this.data.itemId;

    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Cancel this booking?',
          message:
            "Canceling will release your held appointment slot. You'll be able to start a new booking immediately.",
          confirmButtonLabel: 'Yes, cancel booking',
          confirmButtonColor: 'bg-red-700 hover:bg-red-800 focus:ring-red-300',
          onConfirm: () => of(undefined as void),
        },
      })
      .closed.pipe(
        take(1),
        switchMap(confirmed => {
          if (!confirmed) return EMPTY;
          return this.bookingIntentsService.cancelBookingIntent(intentId).pipe(
            tap(() => {
              this.dialogRef.close();
              this.activeIntentService.reload();
              this.dialog.open(BookAppointmentPatientDialogComponent);
            }),
            catchError(err => {
              this.toast.showWarning(resolveCancelErrorMessage(err));
              this.dialogRef.close();
              this.activeIntentService.reload();
              return EMPTY;
            })
          );
        })
      )
      .subscribe();
  }
}

function resolveCancelErrorMessage(err: unknown): string {
  const e = err as { status?: number; error?: { code?: string } };
  if (e?.status === 422 && e?.error?.code === 'INVALID_BOOKING_STATUS') {
    return 'This booking has already been confirmed or is no longer active.';
  }
  if (e?.status === 404) {
    return 'Booking not found. It may have already expired.';
  }
  return 'Unable to cancel booking. Please try again.';
}
